import React, { useRef, useState, useCallback, useEffect } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

export const BeforeAfterSlider = ({ before, after, composite, beforeLabel = "Before", afterLabel = "After" }) => {
    const containerRef = useRef(null);
    const [pos, setPos] = useState(50);
    const [dragging, setDragging] = useState(false);

    const update = useCallback((clientX) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const p = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(0, Math.min(100, p)));
    }, []);

    const onDown = (e) => {
        setDragging(true);
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        update(cx);
    };

    useEffect(() => {
        if (!dragging) return;
        const move = (e) => update(e.touches ? e.touches[0].clientX : e.clientX);
        const up = () => setDragging(false);
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        window.addEventListener("touchmove", move);
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", up);
        };
    }, [dragging, update]);

    return (
        <div
            ref={containerRef}
            onMouseDown={onDown}
            onTouchStart={onDown}
            className={`relative w-full ${composite ? "aspect-square" : "aspect-[4/3]"} rounded-2xl overflow-hidden select-none cursor-ew-resize bento-shadow`}
            data-testid="before-after-slider"
        >
            {/* Before image (bottom layer) */}
            {composite ? (
                <div className="absolute inset-0 overflow-hidden bg-foreground/5" aria-label="Before treatment">
                    <img
                        src={composite}
                        alt="Before treatment"
                        className="absolute max-w-none select-none"
                        style={{ width: "200%", height: "auto", left: 0, top: "50%", transform: "translateY(-50%)" }}
                        draggable={false}
                    />
                </div>
            ) : (
                <img
                    src={before}
                    alt="Before treatment"
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                />
            )}
            {/* Before label */}
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-semibold tracking-wider uppercase z-10">
                {beforeLabel}
            </div>

            {/* After image (clipped top layer) */}
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
                {composite ? (
                    <div className="absolute inset-0 overflow-hidden bg-foreground/5" aria-label="After treatment">
                        <img
                            src={composite}
                            alt="After treatment"
                            className="absolute max-w-none select-none"
                            style={{ width: "200%", height: "auto", right: 0, left: "auto", top: "50%", transform: "translateY(-50%)" }}
                            draggable={false}
                        />
                    </div>
                ) : (
                    <img
                        src={after}
                        alt="After treatment"
                        className="absolute inset-0 w-full h-full object-cover"
                        draggable={false}
                    />
                )}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-primary text-white text-xs font-semibold tracking-wider uppercase z-10">
                    {afterLabel}
                </div>
            </div>

            {/* Divider line + handle */}
            <div
                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none"
                style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-xl flex items-center justify-center">
                    <CaretLeft size={14} weight="bold" color="hsl(192 100% 31%)" />
                    <CaretRight size={14} weight="bold" color="hsl(192 100% 31%)" />
                </div>
            </div>
        </div>
    );
};

export default BeforeAfterSlider;
